import * as React from 'react';

interface Props {
  url: string;
  intervalMs?: number;
}

const DEFAULT_INTERVAL_MS = 5000;

/**
 * Polls a JSON endpoint until it returns `{ redirect_to: string }`,
 * then navigates to that URL. Renders nothing.
 */
const RedirectPoller: React.FC<Props> = ({ url, intervalMs = DEFAULT_INTERVAL_MS }) => {
  React.useEffect(() => {
    let cancelled = false;
    let timeoutId: number | undefined;

    const schedule = (delay: number = intervalMs) => {
      if (cancelled) return;
      if (timeoutId) window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(poll, delay);
    };

    const poll = async () => {
      if (cancelled) return;
      if (document.hidden) {
        schedule();
        return;
      }

      try {
        const res = await fetch(url, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin'
        });
        if (res.ok) {
          const data = await res.json();
          if (data?.redirect_to) {
            window.location.href = data.redirect_to;
            return;
          }
        }
      } catch (_) {
        // transient failure — keep polling
      }

      schedule();
    };

    const onVisibility = () => {
      if (!document.hidden) schedule(0);
    };

    document.addEventListener('visibilitychange', onVisibility);
    schedule();

    return () => {
      cancelled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [url, intervalMs]);

  return null;
};

export default RedirectPoller;
